import type { Location } from 'react-router-dom';
import { useCallback, useContext, useEffect, useState } from 'react';
import { UNSAFE_NavigationContext as NavigationContext } from 'react-router-dom';

type Transition = {
    retry: () => void;
    location: Location;
};

type Blocker = (tx: Transition) => void;

// useBlocker taps into React Router’s internal navigation system and intercepts any navigation attempt.
// When navigation is blocked, we capture the “transition” (the navigation attempt).
const useBlocker = (blocker: Blocker, shouldBlock = true) => {
    const { navigator } = useContext(NavigationContext) as unknown as {
        navigator: {
            block: (cb: (tx: Transition) => void) => () => void;
        };
    };

    useEffect(() => {
        if (!shouldBlock) return;

        const unblock = navigator.block((tx: Transition) => {
            const autoUnblockingTx: Transition = {
                ...tx,
                retry() {
                    unblock();
                    tx.retry();
                },
            };

            blocker(autoUnblockingTx);
        });

        return unblock;
    }, [navigator, blocker, shouldBlock]);
};

//  Allows us to block navigation in React Router and show a custom confirmation dialog
export const useCallbackPrompt = (shouldBlock: boolean, onPrompt?: () => void) => {
    const [lastTx, setLastTx] = useState<Transition | null>(null);

    const blocker = useCallback(
        (tx: Transition) => {
            setLastTx(tx);
            onPrompt?.();
        },
        [onPrompt]
    );

    useBlocker(blocker, shouldBlock);

    const confirmNavigation = useCallback(() => {
        if (lastTx) {
            lastTx.retry(); // continue navigation
            setLastTx(null);
        }
    }, [lastTx]);

    const cancelNavigation = useCallback(() => {
        setLastTx(null);
    }, []);

    return {
        confirmNavigation,
        cancelNavigation,
    };
};
